'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, User, Sparkles, Hash, AtSign } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomUsernameGeneratorContent } from '../../data/random-username-generator-content';
import Link from 'next/link';

type UsernameStyle = 'random' | 'gamer' | 'professional' | 'cute' | 'cool';
type Separator = 'none' | 'dot' | 'underscore' | 'hyphen';

const adjectives = [
    'adventurous', 'brave', 'creative', 'daring', 'energetic', 'fearless', 'gentle', 'happy',
    'innovative', 'joyful', 'kind', 'lucky', 'mystic', 'noble', 'optimistic', 'peaceful',
    'quick', 'radiant', 'sincere', 'talented', 'unique', 'vibrant', 'witty', 'xenial',
    'youthful', 'zealous'
];

const nouns = [
    'ninja', 'wizard', 'dragon', 'phoenix', 'warrior', 'explorer', 'traveler', 'pioneer',
    'champion', 'legend', 'master', 'guru', 'hunter', 'ranger', 'sorcerer', 'assassin',
    'samurai', 'viking', 'knight', 'archer', 'mage', 'monk', 'paladin', 'rogue',
    'warlock', 'druid'
];

const gamerTags = [
    'shadow', 'blade', 'storm', 'fire', 'ice', 'thunder', 'void', 'cosmic', 'neon', 'quantum',
    'alpha', 'beta', 'omega', 'nova', 'vortex', 'phoenix', 'dragon', 'wolf', 'tiger', 'eagle',
    'ghost', 'phantom', 'spectre', 'viper', 'cobra', 'python', 'falcon', 'hawk', 'raven', 'wolf'
];

const professionalTitles = [
    'engineer', 'developer', 'designer', 'architect', 'analyst', 'scientist', 'researcher',
    'consultant', 'specialist', 'expert', 'professional', 'guru', 'ninja', 'wizard',
    'lead', 'director', 'manager', 'pro', 'evangelist', 'advocate'
];

export default function RandomUsernameGenerator() {
    const [username, setUsername] = useState('');
    const [style, setStyle] = useState<UsernameStyle>('random');
    const [length, setLength] = useState<number>(12);
    const [separator, setSeparator] = useState<Separator>('none');
    const [includeNumber, setIncludeNumber] = useState<boolean>(true);
    const [includeSpecialChar, setIncludeSpecialChar] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);
    const [count, setCount] = useState<number>(5);
    const { toast } = useToast();

    const generateRandomNumber = useCallback((min: number, max: number) => {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }, []);

    const getRandomElement = useCallback(<T,>(array: T[]): T => {
        return array[Math.floor(Math.random() * array.length)];
    }, []);

    const generateUsername = useCallback(() => {
        let newUsername = '';
        const sep = separator === 'none' ? '' :
            separator === 'dot' ? '.' :
                separator === 'underscore' ? '_' : '-';

        const getRandomAdjective = () => getRandomElement(adjectives);
        const getRandomNoun = () => getRandomElement(nouns);
        const getRandomGamerTag = () => getRandomElement(gamerTags);
        const getRandomProfessionalTitle = () => getRandomElement(professionalTitles);

        switch (style) {
            case 'gamer':
                newUsername = `${getRandomGamerTag()}`;
                if (Math.random() > 0.3) {
                    newUsername += sep + getRandomGamerTag();
                }
                break;
            case 'professional':
                newUsername = getRandomAdjective();
                if (Math.random() > 0.2) {
                    newUsername += sep + getRandomProfessionalTitle();
                }
                break;
            case 'cute':
                newUsername = getRandomAdjective();
                if (Math.random() > 0.5) {
                    newUsername += sep + getRandomNoun();
                }
                break;
            case 'cool':
                newUsername = getRandomGamerTag();
                if (Math.random() > 0.7) {
                    newUsername += sep + getRandomGamerTag();
                }
                break;
            default: // random
                const patterns = [
                    () => `${getRandomAdjective()}${sep}${getRandomNoun()}`,
                    () => `${getRandomGamerTag()}${sep}${getRandomGamerTag()}`,
                    () => `${getRandomAdjective()}${sep}${getRandomProfessionalTitle()}`,
                    () => `${getRandomNoun()}${generateRandomNumber(100, 9999)}`,
                    () => `The${sep}${getRandomNoun()}`,
                ];
                newUsername = getRandomElement(patterns)();
        }

        // Ensure length is within bounds
        while (newUsername.length < 4) {
            newUsername += sep + getRandomNoun();
        }

        // Trim to max length if needed
        if (newUsername.length > length) {
            newUsername = newUsername.substring(0, length);
        }

        // Add number if needed
        if (includeNumber && newUsername.length + 3 <= length) {
            const num = generateRandomNumber(1, 999);
            newUsername += num < 10 ? '0' + num : num;
        }

        // Add special character if needed
        if (includeSpecialChar && newUsername.length + 1 <= length) {
            const specialChars = '!@#$%^&*';
            const char = specialChars[Math.floor(Math.random() * specialChars.length)];
            newUsername = char + newUsername;
        }

        // Ensure first character is a letter
        if (!/^[a-zA-Z]/.test(newUsername)) {
            newUsername = 'user' + newUsername;
        }

        // Ensure username isn't too short
        while (newUsername.length < 4) {
            newUsername += generateRandomNumber(0, 9);
        }

        // Ensure username isn't too long
        newUsername = newUsername.substring(0, Math.min(length, 30));

        return newUsername;
    }, [style, length, separator, includeNumber, includeSpecialChar, generateRandomNumber]);

    const generateNewUsername = useCallback(() => {
        setUsername(generateUsername());
        setCopied(false);
    }, [generateUsername]);

    const generateMultipleUsernames = useCallback(() => {
        const usernames = [];
        for (let i = 0; i < count; i++) {
            usernames.push(generateUsername());
        }
        return usernames.join('\n');
    }, [count, generateUsername]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Username has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    }, [toast]);

    const handleLengthChange = useCallback((value: number[]) => {
        setLength(value[0]);
    }, []);

    useEffect(() => {
        generateNewUsername();
    }, [generateNewUsername]);

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Random Username Generator">
                Generate random usernames with our free online tool. Create unique, memorable usernames for social media, gaming, and online accounts.
            </ToolPageIntro>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Generate Username</CardTitle>
                            <CardDescription>Create unique and creative usernames</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <div className="relative">
                                            <Input
                                                type="text"
                                                value={username}
                                                readOnly
                                                className="text-lg font-mono h-12 pr-24"
                                                placeholder="Your username will appear here"
                                            />
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex space-x-1">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => copyToClipboard(username)}
                                                    disabled={!username}
                                                    className="h-8 w-8"
                                                    aria-label="Copy to clipboard"
                                                >
                                                    {copied ? (
                                                        <Check className="h-4 w-4" />
                                                    ) : (
                                                        <Copy className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={generateNewUsername}
                                                    className="h-8 w-8"
                                                    aria-label="Generate new username"
                                                >
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={generateNewUsername}
                                        className="h-12 px-4 whitespace-nowrap"
                                    >
                                        <Sparkles className="mr-2 h-4 w-4" />
                                        Generate
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="style" className="block mb-2">Style</Label>
                                    <select
                                        id="style"
                                        value={style}
                                        onChange={(e) => setStyle(e.target.value as UsernameStyle)}
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <option value="random">Random</option>
                                        <option value="gamer">Gamer Tag</option>
                                        <option value="professional">Professional</option>
                                        <option value="cute">Cute</option>
                                        <option value="cool">Cool</option>
                                    </select>
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <Label>Length: {length} characters</Label>
                                        <span className="text-xs text-muted-foreground">
                                            {length < 6 ? 'Short' : length < 12 ? 'Medium' : 'Long'}
                                        </span>
                                    </div>
                                    <Slider
                                        min={4}
                                        max={30}
                                        step={1}
                                        value={[length]}
                                        onValueChange={handleLengthChange}
                                        className="py-4"
                                    />
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>4</span>
                                        <span>30</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="separator" className="block mb-2">Separator</Label>
                                        <select
                                            id="separator"
                                            value={separator}
                                            onChange={(e) => setSeparator(e.target.value as Separator)}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <option value="none">No separator (exampleUser)</option>
                                            <option value="dot">Dot (example.user)</option>
                                            <option value="underscore">Underscore (example_user)</option>
                                            <option value="hyphen">Hyphen (example-user)</option>
                                        </select>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="include-number"
                                                checked={includeNumber}
                                                onCheckedChange={setIncludeNumber}
                                            />
                                            <Label htmlFor="include-number" className="flex items-center">
                                                <Hash className="h-4 w-4 mr-1" />
                                                Include numbers
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="include-special"
                                                checked={includeSpecialChar}
                                                onCheckedChange={setIncludeSpecialChar}
                                            />
                                            <Label htmlFor="include-special" className="flex items-center">
                                                <AtSign className="h-4 w-4 mr-1" />
                                                Special character
                                            </Label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Multiple Usernames</CardTitle>
                            <CardDescription>Generate multiple usernames at once</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <div className="flex-1">
                                        <Label htmlFor="count" className="block mb-2">Number of usernames (1-20)</Label>
                                        <Input
                                            id="count"
                                            type="number"
                                            min="1"
                                            max="20"
                                            value={count}
                                            onChange={(e) => setCount(Math.min(Math.max(1, parseInt(e.target.value) || 1), 20))}
                                            className="w-24"
                                        />
                                    </div>
                                    <Button
                                        onClick={() => copyToClipboard(generateMultipleUsernames())}
                                        className="mt-6"
                                        variant="outline"
                                    >
                                        <Copy className="mr-2 h-4 w-4" />
                                        Copy All
                                    </Button>
                                </div>
                                <div className="bg-muted p-4 rounded-md">
                                    <pre className="text-sm overflow-auto max-h-60">
                                        {generateMultipleUsernames()}
                                    </pre>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>About Username Generator</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p>
                                Create unique and memorable usernames for all your online accounts,
                                gaming profiles, and social media platforms.
                            </p>

                            <div className="space-y-2">
                                <h3 className="font-semibold">Tips for Great Usernames</h3>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                    <li>Keep it simple and easy to remember</li>
                                    <li>Avoid personal information</li>
                                    <li>Use a mix of letters and numbers</li>
                                    <li>Consider your online persona</li>
                                    <li>Check availability on your favorite platforms</li>
                                </ul>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-semibold">Username Ideas</h3>
                                <div className="grid grid-cols-1 gap-2 text-sm">
                                    <div className="bg-muted p-2 rounded">
                                        <div className="font-mono">CosmicExplorer42</div>
                                        <div className="text-xs text-muted-foreground">Style: Random, Length: 16</div>
                                    </div>
                                    <div className="bg-muted p-2 rounded">
                                        <div className="font-mono">ShadowNinja_X</div>
                                        <div className="text-xs text-muted-foreground">Style: Gamer, Underscore</div>
                                    </div>
                                    <div className="bg-muted p-2 rounded">
                                        <div className="font-mono">CreativeMind.Dev</div>
                                        <div className="text-xs text-muted-foreground">Style: Professional, Dot</div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Popular Platforms</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <div className="p-2 hover:bg-muted rounded transition-colors">
                                <div className="font-medium">Twitter</div>
                                <div className="text-muted-foreground">@username (4-15 chars)</div>
                            </div>
                            <div className="p-2 hover:bg-muted rounded transition-colors">
                                <div className="font-medium">Instagram</div>
                                <div className="text-muted-foreground">@username (1-30 chars)</div>
                            </div>
                            <div className="p-2 hover:bg-muted rounded transition-colors">
                                <div className="font-medium">TikTok</div>
                                <div className="text-muted-foreground">@username (3-24 chars)</div>
                            </div>
                            <div className="p-2 hover:bg-muted rounded transition-colors">
                                <div className="font-medium">Discord</div>
                                <div className="text-muted-foreground">Username#0000</div>
                            </div>
                            <div className="p-2 hover:bg-muted rounded transition-colors">
                                <div className="font-medium">Reddit</div>
                                <div className="text-muted-foreground">u/username (3-20 chars)</div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomUsernameGeneratorContent.title}
                description={randomUsernameGeneratorContent.description}
                content={randomUsernameGeneratorContent.content}
                faqs={randomUsernameGeneratorContent.faqs}
                relatedTools={randomUsernameGeneratorContent.relatedTools}
            />
        </div>
    );
}
